import React, { Suspense } from 'react';
import { LoadingSpinner } from '@atoms';
import { ErrorBoundary } from '@organisms';

interface SuspenseWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
  onError?: (error: Error) => void;
}

/**
 * SuspenseWrapper Molecule Component
 * 
 * Combines Suspense and ErrorBoundary for robust async component loading.
 * 
 * @example
 * ```tsx
 * <SuspenseWrapper>
 *   <LazyInvoiceList />
 * </SuspenseWrapper>
 * ```
 */
export const SuspenseWrapper: React.FC<SuspenseWrapperProps> = ({
  children,
  fallback,
  errorFallback,
  onError,
}) => {
  const defaultFallback = (
    <div className="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" text="Loading component..." />
    </div>
  );

  return (
    <ErrorBoundary 
      fallback={errorFallback} 
      onError={onError}
    >
      <Suspense fallback={fallback || defaultFallback}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Hook for creating suspenseful queries
 */
export function useSuspenseQuery<TData>(
  queryFn: () => Promise<TData>,
  deps: React.DependencyList = []
) {
  const [data, setData] = React.useState<TData | null>(null);
  const [promise, setPromise] = React.useState<Promise<TData> | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      const newPromise = queryFn();
      setPromise(newPromise);
      
      try {
        const result = await newPromise;
        if (!cancelled) {
          setData(result);
          setPromise(null);
        }
      } catch (error) {
        if (!cancelled) {
          setPromise(null);
          throw error;
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, deps);

  if (promise) {
    throw promise;
  }

  return data;
}

/**
 * Preloader for warming up components
 */
export class ComponentPreloader {
  private static cache = new Map<string, Promise<any>>();

  static preload<T>(key: string, importFn: () => Promise<T>): Promise<T> {
    if (!this.cache.has(key)) {
      this.cache.set(key, importFn());
    }
    return this.cache.get(key)!;
  }

  static clear(key?: string) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }
}

// Usage example:
// ComponentPreloader.preload('InvoiceList', () => import('../organisms/InvoiceList'));
// const LazyInvoiceList = lazy(() => ComponentPreloader.preload('InvoiceList', () => import('../organisms/InvoiceList')));
